import React, { useCallback, useState } from 'react'

import styled from '@emotion/styled'
import { AuthOptions } from '@nangohq/frontend'

import { useAppUserContext } from 'app/useAppUserContext'
import { useAutomationIntegrations } from 'data/hooks/automations/useAutomationIntegrations'
import { useCreateAutomationConnection } from 'data/hooks/automations/useCreateAutomationConnection'
import { useCreateNangoSessionToken } from 'data/hooks/dataConnections/useCreateNangoSessionToken'
import { INTEGRATION_IDS_REQUIRES_INPUTS } from 'features/DataConnections/constants'
import { ConnectionSetupInputs } from 'features/DataConnections/DataConnectionDetailModal/ConnectionSetupInputs/ConnectionSetupInputs'
import { getNangoClient } from 'features/DataConnections/getNangoClient'

import { useToast } from 'v2/ui/components/useToast'
import useModalToggle from 'v2/ui/utils/useModalToggle'

import { Box } from 'ui/components/Box'
import { Modal, ModalContent, ModalHeader } from 'ui/components/Modal'
import { Spinner } from 'ui/components/Spinner'
import { Headline } from 'ui/components/Text'
import { theme } from 'ui/styling/Theme.css'

import { ADD_NEW_CONNECTION_MODAL_KEY } from './addNewConnectionModalConstants'

const IntegrationCard = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    padding: ${theme.space.l};
    border-radius: ${theme.space.m};
    border: 1px solid ${theme.color.gray200};
    width: 150px;
    &:hover {
        background-color: ${theme.color.gray50};
    }
`

type AddNewConnectionModalProps = {}

export const AddNewConnectionModal: React.FC<AddNewConnectionModalProps> = () => {
    const modal = useModalToggle<{
        integrationId?: AutomationIntegrationId
        onSuccessfulAuth?: (connection: WorkflowIntegrationConnectionDto) => void
    }>(ADD_NEW_CONNECTION_MODAL_KEY)
    const { isOpen, toggle, data: modalParams } = modal

    const onSuccessfulAuth = (connection: WorkflowIntegrationConnectionDto) => {
        toggle()
        modalParams?.onSuccessfulAuth?.(connection)
    }

    return (
        <Modal open={isOpen} onOpenChange={toggle}>
            <ModalContent
                style={{
                    width: '450px',
                }}
            >
                <AddNewConnection
                    onSuccessfulAuth={onSuccessfulAuth}
                    integrationId={modalParams?.integrationId}
                    handleClose={toggle}
                />
            </ModalContent>
        </Modal>
    )
}

type AddNewConnectionProps = {
    // optional, if provided only connections for  this integration can be created
    integrationId?: AutomationIntegrationId
    onSuccessfulAuth?: (connection: WorkflowIntegrationConnectionDto) => void
    handleClose: () => void
}

const AddNewConnection: React.FC<AddNewConnectionProps> = ({
    onSuccessfulAuth,
    integrationId,
    handleClose,
}) => {
    // certain integrations require additional inputs (e.g. username/pw, OAuth overrides, tokens, subdomain overrides etc)
    const [showConnectionSetupInputs, setShowConnectionSetupInputs] = useState<boolean>(false)
    const [selectedIntegration, setSelectedIntegration] = useState<AutomationIntegrationDto>()

    const { data: integrationsData, isLoading: isLoadingIntegrations } = useAutomationIntegrations()
    const { user } = useAppUserContext()
    const availableIntegrations = integrationId
        ? integrationsData?.integrations?.filter((integration) => integration.id === integrationId)
        : integrationsData?.integrations

    const { mutateAsync: createSessionToken } = useCreateNangoSessionToken()
    const { mutateAsync: createAutomationConnection } = useCreateAutomationConnection()

    const toast = useToast()

    const handleAddNewConnection = useCallback(
        async (integration: AutomationIntegrationDto, nangoAuthOptions: AuthOptions = {}) => {
            try {
                const sessionToken = await createSessionToken()
                const nango = getNangoClient(integration.id, sessionToken.token)

                const authResult = await nango.auth(integration.id, nangoAuthOptions)
                toast({ title: `${integration.name} added successfully.`, status: 'success' })
                await createAutomationConnection({
                    integration_id: integration.id,
                    nango_connection_id: authResult.connectionId,
                    name: integration.name,
                    config_data: {
                        enabled: true,
                    },
                })

                const newConnection: WorkflowIntegrationConnectionDto = {
                    id: authResult.connectionId,
                    provider_id: integration.id,
                    user_id: user?._sid ?? '',
                    created_at: new Date().toISOString(),
                }
                onSuccessfulAuth?.(newConnection)
            } catch (err) {
                console.error('Automation connection authentication error:', err)
                toast({
                    title: 'There was a problem authenticating your connection. Please try again later.',
                    type: 'error',
                })
            }
        },
        [createAutomationConnection, createSessionToken, onSuccessfulAuth, toast, user?._sid]
    )

    return (
        <Box flexDirection="column" pb="3xl" width="full" maxHeight="75vh" overflow="scroll">
            {isLoadingIntegrations && (
                <Box
                    flex
                    style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Spinner size="2xl" />
                </Box>
            )}
            {!isLoadingIntegrations && showConnectionSetupInputs && selectedIntegration ? (
                <ConnectionSetupInputs
                    integrationId={selectedIntegration.id}
                    handleConfirmed={async (nangoAuthOptions: AuthOptions) => {
                        setShowConnectionSetupInputs(false)
                        await handleAddNewConnection(selectedIntegration, nangoAuthOptions)
                    }}
                    handleClose={handleClose}
                />
            ) : (
                <>
                    <ModalHeader title="Pick a provider" />

                    <Box
                        pt="2xl"
                        px="2xl"
                        style={{
                            display: 'grid',
                            columnGap: theme.space.xl,
                            rowGap: theme.space.xl,
                            gridTemplateColumns: 'repeat(2, 1fr)',
                        }}
                    >
                        {availableIntegrations?.map((integration: AutomationIntegrationDto) => (
                            <ConnectionAuthenticator
                                key={integration.id}
                                integration={integration}
                                handleAddNewConnection={handleAddNewConnection}
                                setShowConnectionSetupInputs={setShowConnectionSetupInputs}
                                setSelectedIntegration={setSelectedIntegration}
                            />
                        ))}
                    </Box>
                </>
            )}
        </Box>
    )
}

type ConnectionAuthenticatorProps = {
    integration: AutomationIntegrationDto
    setShowConnectionSetupInputs: (show: boolean) => void
    handleAddNewConnection: (integration: AutomationIntegrationDto) => Promise<void>
    setSelectedIntegration: (integration: AutomationIntegrationDto) => void
}

const ConnectionAuthenticator: React.FC<ConnectionAuthenticatorProps> = ({
    integration,
    setShowConnectionSetupInputs,
    handleAddNewConnection,
    setSelectedIntegration,
}) => {
    const handleAuth = async () => {
        if (INTEGRATION_IDS_REQUIRES_INPUTS.includes(integration.id)) {
            setSelectedIntegration(integration)
            setShowConnectionSetupInputs(true)
        } else {
            handleAddNewConnection(integration)
        }
    }

    return (
        <IntegrationCard onClick={handleAuth}>
            <Box
                style={{
                    borderRadius: '48px',
                    height: '48px',
                    width: '48px',
                    padding: '8px',
                    border: `1px solid ${theme.color.gray200}`,
                }}
            >
                <img src={integration.image_url} alt={`${integration.name} logo`} />
            </Box>
            <Box pt="xl" flex flexDirection="column">
                <Headline size="2xs">{integration.name}</Headline>
            </Box>
        </IntegrationCard>
    )
}
